import { Box, Button, Divider, Text } from "@mantine/core";
import { openConfirmModal } from "@mantine/modals";
import { IconShoppingCartPlus } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import useCreateNewOrder from "../../hooks/useCreateNewOrder";

interface IPlaceNewOrderModalProps {
  organizationShortId: string;
  orderId: string;
}

export default function PlaceNewOrderModal({
  organizationShortId,
  orderId,
}: IPlaceNewOrderModalProps) {
  const { t } = useTranslation();
  const { mutate } = useCreateNewOrder();

  const openModal = () =>
    openConfirmModal({
      title: t("v2.OrderPage.placeNewOrder.modalTitle"),
      children: (
        <Box>
          <Divider color="var(--divider)" mb={20} />
          <Text
            style={{
              fontSize: "var(--p)",
              color: "var(--body)",
              lineHeight: "160%",
            }}
          >
            {t("v2.OrderPage.placeNewOrder.modalText")}
          </Text>
        </Box>
      ),
      centered: true,
      radius: 20,
      styles: {
        content: {
          border: "1px solid var(--divider)",
          width: "min(420px, calc(100vw - 32px))",
        },
        header: {
          padding: "20px 20px 16px",
        },
        title: {
          fontWeight: 600,
          fontSize: 17,
          color: "var(--heading)",
        },
        body: {
          padding: "0 20px 20px",
        },
      },
      labels: {
        confirm: t("v2.OrderPage.placeNewOrder.modalConfirm"),
        cancel: t("v2.OrderPage.placeNewOrder.modalCancel"),
      },
      groupProps: {
        style: { gap: 12, marginTop: 20 },
      },
      confirmProps: {
        variant: "filled",
        fullWidth: true,
        sx: {
          flex: 1,
          background: "var(--accent-gr)",
          color: "#fff",
          fontWeight: 600,
          fontSize: "var(--p)",
          borderRadius: 48,
          border: "none",
          height: 46,
          transition: "transform 0.2s ease",
          "&:hover": {
            background: "var(--accent-gr)",
            transform: "scale(1.02)",
          },
        },
      },
      cancelProps: {
        variant: "outline",
        fullWidth: true,
        sx: {
          flex: 1,
          color: "var(--heading)",
          borderColor: "var(--divider)",
          fontWeight: 600,
          fontSize: "var(--p)",
          borderRadius: 48,
          height: 46,
          background: "transparent",
          transition: "background 0.2s ease, border-color 0.2s ease",
          "&:hover": {
            background: "var(--divider20)",
            borderColor: "var(--heading)",
          },
        },
      },
      onConfirm: () => {
        mutate({
          organizationShortId,
          orderId,
        });
      },
    });

  return (
    <Button
      fullWidth
      radius="xl"
      size="md"
      leftIcon={<IconShoppingCartPlus size={18} strokeWidth={2.2} />}
      onClick={() => openModal()}
      sx={{
        background: "var(--accent-gr)",
        color: "#fff",
        fontWeight: 600,
        border: "none",
        "&:hover": { background: "var(--accent-gr)", opacity: 0.9 },
      }}
    >
      {t("v2.OrderPage.placeNewOrder.button")}
    </Button>
  );
}
